// THE §4.5 CUT SCREEN'S DATA LAYER (slice 07-C-3) — five routes, all ONLINE.
//
// D-166 IS THE SHAPE OF THIS FILE. "Nothing on this screen queues": a guided
// cut consumes a physical unit, mints two, posts a waste ledger row, creates two
// inspections and closes a third, and there is no un-cutting a cable. §9.3's own
// `QUALITY_CUT_LENGTH_CHANGED` 409 exists BECAUSE the source length can go stale
// between propose and execute — which is precisely the staleness a queued cut
// could not detect. So none of these five touches the floor outbox, and the
// floor gains NO new operation kind (which is the ruling's stated payoff: no new
// op-kind contract, and none of its two silent obligations).
//
// EVERY CALL COMES BACK AS A VALUE, NEVER A THROW. The refusals on this surface
// are DECISIONS with continuations inside them — `QUALITY_CUT_LOT_HELD` names
// the lot hold, `QUALITY_CUT_SUBJECT_HELD` names the other inspection's
// quarantine and its author, `QUALITY_CUT_SUBJECT_QUARANTINED` names module 06's
// own release door, `QUALITY_CUT_NOT_ELIGIBLE` names the scrap door,
// `QUALITY_CUT_LENGTH_CHANGED` carries the fresh length in its extensions — and
// a screen that wrapped these in a generic error toast would throw away the only
// text the operator can act on. The `FailureActionOutcome` vocabulary is the
// §4.4 sheet's, reused rather than re-invented: one shape for "the server
// refused, here is its sentence" across the whole module.
//
// THE IDEMPOTENCY KEY IS PER INTENT, NOT PER ATTEMPT — the same rule and the
// same reasoning as `use-failure-action-arm.ts` (R1), and it matters more here
// than anywhere else in the product: a retried EXECUTE under a fresh key after a
// lost response is a saw stroke taken twice.

import { useCallback, useMemo, useRef, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { client } from "../../../lib/api";
import { UnauthorizedError } from "../../../lib/problem";
import { newIdempotencyKey, RequestError, unwrap } from "../../../lib/request";
import type { FailureActionOutcome } from "../../office/quality/run/inspection-run-port";
import type {
  QualityCutEventDetail,
  QualityCutExecuteResponse,
} from "../../office/quality/hooks/quality-queries";
import { useOnline } from "./use-online";

/** One operator adjustment of the removed window (§5's confirm row). The screen
 * sends exactly one — the WASTE segment's new extent — because the service
 * reduces any adjustment list to that single fact anyway
 * (`windowFromAdjustments`), and sending the pieces the server is about to
 * recompute would be the client claiming an authority §4.5 explicitly denies it:
 * "the client's arithmetic is a courtesy, never the authority". */
export interface CutWindowAdjustment {
  readonly seq: number;
  readonly from_m: string;
  readonly to_m: string;
}

export interface GuidedCutArm {
  /** `GET /v1/quality/cut-events/{id}` — the §2.8 row plus its segments. */
  read(id: string): Promise<FailureActionOutcome<QualityCutEventDetail>>;
  /** `POST /v1/quality/inspections/{id}/cut-events` — §3.5d step 1. The EMPTY
   * body is the request the floor sends, and D-181 pt 3 makes
   * `guided_cut.eligible === true` a promise that exactly this call succeeds. */
  propose(inspectionId: string): Promise<FailureActionOutcome<QualityCutEventDetail>>;
  /** `POST /v1/quality/cut-events/{id}/confirm` — §3.5d step 2. */
  confirm(input: {
    readonly id: string;
    readonly adjustment: CutWindowAdjustment | undefined;
  }): Promise<FailureActionOutcome<QualityCutEventDetail>>;
  /** `POST /v1/quality/cut-events/{id}/execute` — §3.5d step 3, the one
   * transaction. */
  execute(id: string): Promise<FailureActionOutcome<QualityCutExecuteResponse>>;
  /** `POST /v1/quality/cut-events/{id}/cancel` — service-narrowed to
   * {group_admin, facility_admin, qc} on top of the single code (D-163). A
   * floor_worker's cancel comes back 403 and the screen renders the server's
   * own sentence: the narrowing is the server's to state, and a client that
   * hid the control would be mirroring a rule §7's cell spells as "✓ (not
   * cancel)" — one code, one service narrowing — into a second place that can
   * drift. The operator is never stranded either way: D-162's supersede means
   * their next propose replaces this cut. */
  cancel(input: {
    readonly id: string;
    readonly reason: string;
  }): Promise<FailureActionOutcome<QualityCutEventDetail>>;
  readonly busy: boolean;
  readonly online: boolean;
}

function refusedOf(error: unknown): FailureActionOutcome<never> {
  if (error instanceof RequestError) {
    return {
      kind: "refused",
      code: error.code,
      message: error.message,
      detail: error.problem?.detail,
      requestId: error.requestId,
    };
  }
  return {
    kind: "refused",
    code: undefined,
    message: error instanceof Error ? error.message : String(error),
    detail: undefined,
    requestId: undefined,
  };
}

interface Intent {
  readonly fingerprint: string;
  readonly key: string;
}

function keyFor(intents: Map<string, Intent>, scope: string, fingerprint: string): string {
  const prior = intents.get(scope);
  if (prior?.fingerprint === fingerprint) return prior.key;
  const key = newIdempotencyKey();
  intents.set(scope, { fingerprint, key });
  return key;
}

function settleIntent(
  intents: Map<string, Intent>,
  scope: string,
  outcome: FailureActionOutcome<unknown>,
): void {
  // Keep the key ONLY across a transport failure, where no server decision
  // exists and the retry must replay into the stored answer. A coded refusal or
  // a success means the server saw the intent.
  if (outcome.kind === "refused" && outcome.code === undefined) return;
  intents.delete(scope);
}

export function useGuidedCutArm(): GuidedCutArm {
  const online = useOnline();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const intents = useRef(new Map<string, Intent>());

  const signedOut = useCallback((): FailureActionOutcome<never> => {
    void navigate({ to: "/login" });
    return {
      kind: "refused",
      code: "UNAUTHENTICATED",
      message: t("quality.run.signedOut"),
      detail: undefined,
      requestId: undefined,
    };
  }, [navigate, t]);

  /** The one send wrapper: busy flag, 401 redirect, per-intent key, settlement.
   * Five routes through one funnel so a refusal cannot be handled four ways. */
  const send = useCallback(
    async <T>(
      scope: string,
      fingerprint: string,
      call: (key: string) => Promise<T>,
    ): Promise<FailureActionOutcome<T>> => {
      setBusy(true);
      const key = keyFor(intents.current, scope, fingerprint);
      let outcome: FailureActionOutcome<T>;
      try {
        outcome = { kind: "done", result: await call(key) };
      } catch (error) {
        outcome = error instanceof UnauthorizedError ? signedOut() : refusedOf(error);
      } finally {
        setBusy(false);
      }
      settleIntent(intents.current, scope, outcome);
      return outcome;
    },
    [signedOut],
  );

  const read = useCallback<GuidedCutArm["read"]>(
    async (id) => {
      setBusy(true);
      try {
        const res = await client.GET("/v1/quality/cut-events/{id}", {
          params: { path: { id } },
        });
        return { kind: "done", result: unwrap(res, "Could not load the cut") };
      } catch (error) {
        return error instanceof UnauthorizedError ? signedOut() : refusedOf(error);
      } finally {
        setBusy(false);
      }
    },
    [signedOut],
  );

  const propose = useCallback<GuidedCutArm["propose"]>(
    async (inspectionId) =>
      send(`cut-propose:${inspectionId}`, "empty", async (key) =>
        unwrap(
          await client.POST("/v1/quality/inspections/{id}/cut-events", {
            params: { path: { id: inspectionId }, header: { "Idempotency-Key": key } },
            body: {},
          }),
          "Could not plan the cut",
        ),
      ),
    [send],
  );

  const confirm = useCallback<GuidedCutArm["confirm"]>(
    async (input) => {
      const body = input.adjustment === undefined ? {} : { adjustments: [{ ...input.adjustment }] };
      return send(`cut-confirm:${input.id}`, JSON.stringify(body), async (key) =>
        unwrap(
          await client.POST("/v1/quality/cut-events/{id}/confirm", {
            params: { path: { id: input.id }, header: { "Idempotency-Key": key } },
            body,
          }),
          "Could not confirm the cut",
        ),
      );
    },
    [send],
  );

  const execute = useCallback<GuidedCutArm["execute"]>(
    async (id) =>
      // The fingerprint is the cut event itself: an execute has no body, so
      // every attempt at this cut IS the same intent, and the key must survive
      // for the retry of a lost response. That is the whole of §9.1 applied to
      // the one call in this module that physically destroys stock.
      send(`cut-execute:${id}`, id, async (key) =>
        unwrap(
          await client.POST("/v1/quality/cut-events/{id}/execute", {
            params: { path: { id }, header: { "Idempotency-Key": key } },
          }),
          "Could not execute the cut",
        ),
      ),
    [send],
  );

  const cancel = useCallback<GuidedCutArm["cancel"]>(
    async (input) =>
      send(`cut-cancel:${input.id}`, input.reason, async (key) =>
        unwrap(
          await client.POST("/v1/quality/cut-events/{id}/cancel", {
            params: { path: { id: input.id }, header: { "Idempotency-Key": key } },
            body: { cancelled_reason: input.reason },
          }),
          "Could not cancel the cut",
        ),
      ),
    [send],
  );

  // A STABLE OBJECT. The screen's "load the live cut once" effect lists this
  // arm in its dependency array, and a fresh literal on every render would make
  // that effect re-run on every render — harmless only because a ref guards it,
  // which is exactly the kind of "harmless" that stops being harmless when
  // somebody adds a second effect.
  return useMemo(
    () => ({ read, propose, confirm, execute, cancel, busy, online }),
    [read, propose, confirm, execute, cancel, busy, online],
  );
}
